import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { skillstreeApi } from '../services/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import './Skillstree.css';

interface Category {
  id: number;
  title: string;
  description: string;
  image: string;
}

const Skillstree = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Map category slug/name to image path (keeping existing paths)
  const getImagePath = (slug: string, name: string): string => {
    const imageMap: Record<string, string> = {
      'big-data': '/big_data.jpg',
      'digital-marketing': '/digital_marketing.jpg',
      'cloud-computing': '/cloud_computing.jpg',
      'cyber-security': '/cyber_security.jpg',
      'cybersecurity': '/cyber_security.jpg',
      'digital-product-design': '/digital_design.jpg',
    };

    // Try slug first, then name (lowercased)
    const key = slug?.toLowerCase() || name?.toLowerCase().replace(/\s+/g, '-');
    return imageMap[key] || '/big_data.jpg'; // Default fallback
  };

  // Fetch categories from API using /v1/core-category endpoint
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await skillstreeApi.getCoreCategories();
        const apiData = (response.data as any).data; // API returns { status, data: [...], message }

        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
          throw new Error('No categories found');
        }

        const mappedCategories = apiData.map((cat: any) => {
          const categoryName = cat.name || cat.title || 'Category';
          const categorySlug = cat.slug || '';

          return {
            id: cat.id,
            title: categoryName,
            description: cat.description || '',
            image: getImagePath(categorySlug, categoryName),
          };
        });

        setCategories(mappedCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError(err instanceof Error ? err.message : 'Failed to load categories');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6 flex items-center justify-center animate-fade-in">
        <LoadingSpinner text="Loading categories..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-center">Error</h1>
          <p className="text-center text-red-400 mb-4 text-sm sm:text-base">{error}</p>
          <div className="text-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm sm:text-base"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (categories.length === 0) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">CORE CATEGORIES</h1>
          <p className="text-center text-gray-400 text-sm sm:text-base">No categories available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6 relative pb-24">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Breadcrumbs */}
        <div className="mb-4 mt-4 sm:mb-6 sm:mt-6">
          <nav className="breadcrumbs-container">
            <img 
              src="/breadcrumb_icon.svg" 
              alt="Dashboard" 
              className="breadcrumb-icon"
            />
            <span className="breadcrumb-separator"> {'>'} </span>
            <span className="breadcrumb-text breadcrumb-active">Core Categories</span>
          </nav>
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">CORE CATEGORIES</h1>
        
        {/* Categories Grid Container */}
        <div className="categories-container mx-auto">
        {/* Top Row - 3 categories */}
        <div className="categories-grid">
          {categories.slice(0, 3).map((category) => (
            <div
              key={category.id}
              className="category-card"
              onClick={() => navigate(`/skillstree/${category.id}`)}
            >
              {/* Behind Box - Orange background (shadow effect) */}
              <div className="behind-box">
                {/* Just the orange background - no content */}
              </div>

              {/* Front Box - Contains image and title */}
              <div className="front-overlay-box">
                {/* Image container */}
                <div
                  className="image-container"
                  style={{
                    backgroundImage: `url(${category.image})`
                  }}
                >
                  {/* Centered Title */}
                  <h3 className="text-white font-bold category-title">
                    {category.title}
                  </h3>
                </div>
              </div>
              
              {/* Description - positioned below the layered boxes */}
              <div className="category-description">
                <p className="text-sm text-gray-300 text-left">{category.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Row - 2 categories (left-aligned) */}
        <div className="categories-grid">
          {/* Two cards aligned to the left */}
          {categories.slice(3, 5).map((category) => (
            <div
              key={category.id}
              className="category-card"
              onClick={() => navigate(`/skillstree/${category.id}`)}
            >
              {/* Behind Box - Orange background (shadow effect) */}
              <div className="behind-box">
                {/* Just the orange background - no content */}
              </div>

              {/* Front Box - Contains image and title */}
              <div className="front-overlay-box">
                {/* Image container */}
                <div
                  className="image-container"
                  style={{
                    backgroundImage: `url(${category.image})`
                  }}
                >
                  {/* Centered Title */}
                  <h3 className="text-white font-bold category-title">
                    {category.title}
                  </h3>
                </div>
              </div>
              
              {/* Description - positioned below the layered boxes */}
              <div className="category-description">
                <p className="text-sm text-gray-300 text-left">{category.description}</p>
              </div>
            </div>
          ))}
        </div>
        </div>
      </div>
    </div>
  );
};

export default Skillstree;